/**
 * CoordinatorSelector — Inline coordinator assignment on member demographics
 * Phase 2 Issue 11b
 */

import React, { useState, useEffect } from 'react';
import { UserCheck, Loader2 } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/services/apiClient';

interface User {
  id: number;
  name: string;
  role?: string;
}

interface Props {
  memberId: number;
  currentCoordinatorId: number | null | undefined;
  onChanged?: (newId: number | null) => void;
}

// Denylist, NOT allowlist: a CBO can define ANY org role (e.g. "Disaster Case
// Manager" / dcm01, partner_dcm) and those people must be assignable as case
// managers. We only EXCLUDE platform/internal roles (super_admin, ops_admin,
// platform_admin) and portal members — an allowlist of role keys silently
// dropped every custom org role.
const EXCLUDED_ROLES = ['super_admin', 'ops_admin', 'platform_admin', 'member'];
const INTERNAL_DOMAINS = ['@trust.care', '@weavein.ai', '@payssurance.com', '@anthropic.com'];
const isInternalEmail = (email?: string) =>
  !!email && INTERNAL_DOMAINS.some(d => email.toLowerCase().endsWith(d));

export default function CoordinatorSelector({ memberId, currentCoordinatorId, onChanged }: Props) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [coordinatorId, setCoordinatorId] = useState<number | null>(currentCoordinatorId ?? null);

  useEffect(() => {
    const cboId = localStorage.getItem('active_cbo_id');
    // exclude_internal=true filters out platform staff (super_admin/ops_admin)
    // and Trust-WeaveIn internal email domains server-side. Frontend filter
    // remains as belt-and-suspenders.
    apiClient.get(`/api/cbos/${cboId}/users?exclude_internal=true`)
      .then(resp => {
        const all = Array.isArray(resp.data) ? resp.data : (resp.data?.users || []);
        const staff = all
          .filter((u: any) => {
            const role = (u.user_org_role || u.role || '').toLowerCase();
            if (EXCLUDED_ROLES.includes(role)) return false;
            if (isInternalEmail(u.email)) return false;
            return true;
          })
          .map((u: any) => ({
            id: u.id,
            name: u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
            role: u.user_org_role || u.role,
          }));
        setUsers(staff);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentCoordinatorId]);

  const handleChange = async (value: string) => {
    const newId = value === 'none' ? null : Number(value);
    setSaving(true);
    try {
      const cboId = localStorage.getItem('active_cbo_id');
      await apiClient.put(`/api/members/${memberId}/coordinator`, {
        coordinator_user_id: newId,
      }, {
        headers: { 'X-CBO-ID': cboId || '' },
      });
      setCoordinatorId(newId);
      onChanged?.(newId);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to assign coordinator');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="flex items-center gap-2">
      <UserCheck className="w-4 h-4 text-gray-400 flex-shrink-0" />
      <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">Case Manager</span>
      <div className="w-52">
        <Select
          value={coordinatorId ? String(coordinatorId) : 'none'}
          onValueChange={handleChange}
          disabled={saving}
        >
          <SelectTrigger className="h-8 text-sm">
            {saving ? (
              <div className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving...</div>
            ) : (
              <SelectValue />
            )}
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">
              <span className="text-gray-400">— No case manager —</span>
            </SelectItem>
            {users.map(u => (
              <SelectItem key={u.id} value={String(u.id)}>
                {u.name} <span className="text-gray-400 text-xs ml-1">({u.role})</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
